import { useRef } from "react";

/**
 * A custom hook for creating a debounced function that ensures sequential execution of API calls.
 * @param apiCall - The API function to call (must return a Promise of any type).
 * @param delay - The debounce delay in milliseconds.
 * @returns A debounced function.
 */
export function useDebouncedFunction<T, R>(
  apiCall: (params: T) => Promise<R>, // Allow the callback to return any Promise
  delay: number
): (params: T) => Promise<R> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inProgressRef = useRef(false);
  const lastParamsRef = useRef<T | null>(null);

  const debouncedFunction = async (params: T): Promise<R> => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    return new Promise((resolve, reject) => {
      timeoutRef.current = setTimeout(async () => {
        if (inProgressRef.current) {
          // If an API call is in progress, save the latest parameters
          lastParamsRef.current = params;
          return;
        }

        try {
          inProgressRef.current = true;
          const result = await apiCall(params); // Call the API
          inProgressRef.current = false;

          // Process the last queued parameters
          if (lastParamsRef.current !== null) {
            const pendingParams = lastParamsRef.current;
            lastParamsRef.current = null;
            resolve(await debouncedFunction(pendingParams)); // Recursively handle queued requests
          } else {
            resolve(result);
          }
        } catch (error) {
          inProgressRef.current = false;
          reject(error);
        }
      }, delay);
    });
  };

  return debouncedFunction;
}
